import stateCityDictionary, {
  formatName,
  urlifyName,
} from "../../constants/stateCityDictionary"

import { Anchor } from "../../app/components/anchor"
import PetStateCard from "./PetStateCard"
import PrimaryButton from "../../components/buttons/PrimaryButton"
import React from "react"

import { useRouter } from "next/router"

interface IRecentPetTransports {
  topExportShipments: any[]
  topImportShipments: any[]
  isState?: boolean
}

export default function RecentPetTransports({
  topExportShipments,
  topImportShipments,
}: IRecentPetTransports) {
  const router = useRouter()
  const { city, state } = router.query

  const formattedState = formatName(state as string)
  const urlifiedCity = city ? urlifyName(city as string) : ""

  const originalCity =
    stateCityDictionary[formattedState]?.cities.find(
      (dictCity) => urlifyName(dictCity) === urlifiedCity,
    ) ?? ""

  const formattedCity = originalCity ? formatName(originalCity) : ""
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""

  const currentLocation = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState

  const safelyFormattedCity = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState

  const recentlyFormattedCity = formattedCity
    ? `${formattedCity}`
    : formattedState

  // If both arrays are empty, return empty HTML
  if (
    (!topExportShipments || topExportShipments.length === 0) &&
    (!topImportShipments || topImportShipments.length === 0)
  ) {
    return <></>
  }

  // Check if only one section has data
  const hasExportData = topExportShipments && topExportShipments.length > 0
  const hasImportData = topImportShipments && topImportShipments.length > 0
  const onlyOneSection = hasExportData !== hasImportData

  return (
    <section className="w-full my-16 xl:my-32 flex flex-col items-center">
      <h2 className="text-3xl md:text-4xl mb-10 font-merriweather leading-[60px] md:leading-[72px] text-left md:text-center">
        What {recentlyFormattedCity} pet owners are paying for transport
      </h2>

      <section
        // Anchor for the visual-regression gate, which strips this block before
        // capture: the card count comes from live marketplace data, so its
        // height varies. The <h2> and CTA outside it stay in the pixel diff.
        //
        // Scoped to this grid rather than to the mapped cards inside it — unlike
        // the transporters list — because the grid's own layout is data-dependent
        // (`onlyOneSection` switches it between one and two columns), so it can't
        // be left in the capture either. That takes the two column headings with
        // it. See cypress/e2e/pet-shipping-visual.cy.ts.
        data-testid="recent-pet-transports-cards"
        className={`w-full ${
          onlyOneSection
            ? "flex justify-center"
            : "grid grid-cols-1 lg:grid-cols-2"
        } gap-4`}
      >
        {hasExportData && (
          <div>
            <h3 className="mb-6 font-inter mx-auto text-center text-2xl">
              {safelyFormattedCity} to Other Cities
            </h3>
            {topExportShipments?.map((shipment, i) => (
              // @ts-ignore
              <PetStateCard
                key={`RecentPetTransports/topExportShipments/${i}`}
                // @ts-ignore
                {...shipment}
                type="export"
                currentLocation={currentLocation}
              />
            ))}
          </div>
        )}
        {hasImportData && (
          <div>
            <h3 className="mb-6 font-inter mx-auto text-center text-2xl">
              Other Cities to {safelyFormattedCity}
            </h3>
            {topImportShipments?.map((shipment, i) => (
              // @ts-ignore
              <PetStateCard
                key={`RecentPetTransports/topImportShipments/${i}`}
                // @ts-ignore
                {...shipment}
                type="import"
                currentLocation={currentLocation}
              />
            ))}
          </div>
        )}
      </section>

      <Anchor className="mt-16 mx-auto" href="/shipments/new/animals" passHref>
        <PrimaryButton className="mx-auto rounded-lg">
          Find A Loving Pet Transporter
        </PrimaryButton>
      </Anchor>
    </section>
  )
}
